import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHelmet from "../../Components/SEOHelmet/SEOHelmet";
import blogService from "../../Services/blogService";
import type { Blog } from "../../Types/blog.types";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CategoryIcon from "@mui/icons-material/Category";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "./SingleBlogPost.css";

const SingleBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [popularBlogs, setPopularBlogs] = useState<Blog[]>([]);
  const [recentBlogs, setRecentBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [liked, setLiked] = useState<boolean>(false);
  const [isLiking, setisLiking] = useState<boolean>(false);
  const [likedCount, setLikedCount] = useState<number>(0);

  const loadBlogPost = useCallback(async (): Promise<void> => {
    if (!slug) return;

    try {
      setLoading(true);
      const response = await blogService.getBlogBySlug(slug);

      if (response.success && !Array.isArray(response.data)) {
        setBlog(response.data);

        // Increment views
        if (response.data._id) {
          await blogService.incrementViews(response.data._id);
        }
      }
    } catch (error) {
      console.error("Error loading blog post:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadSidebarData = useCallback(async (): Promise<void> => {
    try {
      // Load related blogs
      if (slug) {
        const relatedResponse = await blogService.getRelatedBlogs(slug, 3);
        if (Array.isArray(relatedResponse.data)) {
          setRelatedBlogs(relatedResponse.data);
        }
      }

      // Load popular blogs
      const popularResponse = await blogService.getPopularBlogs(5);
      if (Array.isArray(popularResponse.data)) {
        setPopularBlogs(popularResponse.data);
      }

      // Load recent blogs
      const recentResponse = await blogService.getRecentBlogs(5);
      if (Array.isArray(recentResponse.data)) {
        setRecentBlogs(recentResponse.data);
      }
    } catch (error) {
      console.error("Error loading sidebar data:", error);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      loadBlogPost();
      loadSidebarData();
    }
  }, [slug, loadBlogPost, loadSidebarData]);

  useEffect(() => {
    if (blog) {
      setLikedCount(blog.likes);

      const likedPosts = JSON.parse(localStorage.getItem("likedPosts") || "[]");
      setLiked(likedPosts.includes(blog._id));
    }
  }, [blog]);

  const handleLike = async (): Promise<void> => {
    if (!blog || isLiking) return;

    const wasLiked = liked;
    const previousCount = likedCount;

    try {
      setisLiking(true);

      setLiked(!liked);
      setLikedCount(liked ? likedCount - 1 : likedCount + 1);

      const likedPosts = JSON.parse(localStorage.getItem("likedPosts") || "[]");

      if (liked) {
        await blogService.decrementLikes(blog._id);

        const updateLikedPosts = likedPosts.filter(
          (id: string) => id !== blog._id
        );
        localStorage.setItem("likedPosts", JSON.stringify(updateLikedPosts));
      } else {
        await blogService.incrementLikes(blog._id);

        if (!likedPosts.includes(blog._id)) {
          likedPosts.push(blog._id);
          localStorage.setItem("likedPosts", JSON.stringify(likedPosts));
        }
      }
    } catch (error: any) {
      console.error("Error toggling like", error);

      setLiked(wasLiked);
      setLikedCount(previousCount);
    } finally {
      setisLiking(false);
    }
  };

  const handleShare = async (): Promise<void> => {
    if (!blog) return;

    const shareData = {
      title: blog.title,
      text: blog.excerpt,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const getAuthorName = (author: any): string => {
    if (!author) return "Lico Team";
    if (typeof author === "string") return author;
    if (typeof author === "object" && author.name) return author.name;
    return "Lico Team";
  };

  const getAuthorDetails = (author: any) => {
    if (!author) {
      return {
        name: "Lico Team",
        title: "Content Team",
        bio: "Official content team at LICO Technologies",
        avatar: null,
      };
    }

    if (typeof author === "string") {
      return {
        name: author,
        title: "Content Writer",
        bio: `${author} is a writer at LICO Technologies`,
        avatar: null,
      };
    }

    if (typeof author === "object") {
      return {
        name: author.name || "Lico Team",
        title: author.title || "Content Writer",
        bio: author.bio || `${author.name} is a writer at LICO Technologies`,
        avatar: author.avatar || null,
      };
    }

    return {
      name: "Lico Team",
      title: "Content Team",
      bio: "Official content team at LICO Technologies",
      avatar: null,
    };
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Generate avatar URL from title
  const getAvatarUrl = (title: string) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      title
    )}&size=400&background=FF9933&color=fff&bold=true&font-size=0.4`;
  };

  if (loading) {
    return (
      <div>
        <div className="blog-loading-container">
          <div className="blog-loading-content">
            <div className="loading-spinner-large"></div>
            <h2>Loading Article...</h2>
            <p>Please wait while we fetch the content</p>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div>
        <div className="error-container">
          <div className="error-icon">📄</div>
          <h2>Blog post not found</h2>
          <p>
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const authorInfo = getAuthorDetails(blog.author);
  const imageUrl = blog.featuredImage || getAvatarUrl(blog.title);

  return (
    <div>
      <SEOHelmet
        title={`${blog.title} - Lico Blog`}
        description={blog.excerpt}
        keywords={blog.tags.join(", ")}
        canonicalUrl={`${window.location.origin}/blog/${blog.slug}`}
      />

      <article className="modern-blog-post">
        {/* Breadcrumb Navigation */}
        <div className="container">
          <div className="breadcrumb-modern">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blog</Link>
            <span>/</span>
            <span className="category-link">{blog.category}</span>
          </div>
        </div>

        {/* Article Header */}
        <header className="article-header">
          <div className="container">
            <div className="header-content">
              <span className="category-badge">{blog.category}</span>

              <h1 className="article-title">{blog.title}</h1>

              <p className="article-excerpt">{blog.excerpt}</p>

              <div className="article-meta-row">
                <div className="author-info-inline">
                  <div className="author-avatar-small">
                    {authorInfo.avatar ? (
                      <img src={authorInfo.avatar} alt={authorInfo.name} />
                    ) : (
                      <PersonIcon />
                    )}
                  </div>
                  <div>
                    <div className="author-name">{authorInfo.name}</div>
                    <div className="author-title-small">{authorInfo.title}</div>
                  </div>
                </div>

                <div className="meta-stats">
                  <div className="meta-stat">
                    <CalendarTodayIcon />
                    <span>{formatDate(blog.publishedAt)}</span>
                  </div>
                  <div className="meta-stat">
                    <AccessTimeIcon />
                    <span>{blog.readTime}</span>
                  </div>
                  <div className="meta-stat">
                    <VisibilityIcon />
                    <span>{blog.views} views</span>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="article-tags">
                {blog.tags.map((tag, index) => (
                  <span key={index} className="tag-pill">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Content Section */}
        <section className="article-content-section">
          <div className="container">
            <div className="content-layout">
              {/* Main Content */}
              <div className="main-article-content">
                {/* Featured Image - Now INSIDE content column */}
                <div className="featured-image-modern">
                  <img
                    src={imageUrl}
                    alt={blog.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = getAvatarUrl(blog.title);
                    }}
                  />
                </div>

                {/* Social Share Buttons */}
                <div className="social-share-sticky">
                  <button
                    onClick={handleLike}
                    className={`social-btn ${liked ? "liked" : ""}`}
                    disabled={isLiking}
                    aria-label={liked ? "Unlike post" : "Like post"}
                  >
                    {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    <span className="count">{likedCount}</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="social-btn"
                    aria-label="Share post"
                  >
                    <ShareIcon />
                  </button>
                </div>

                {/* Article Body */}
                <div
                  className="article-body"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />

                {/* Author Card */}
                <div className="author-card-modern">
                  <div className="author-avatar-large">
                    {authorInfo.avatar ? (
                      <img src={authorInfo.avatar} alt={authorInfo.name} />
                    ) : (
                      <PersonIcon />
                    )}
                  </div>
                  <div className="author-info-detailed">
                    <div className="author-name-large">{authorInfo.name}</div>
                    <div className="author-title-large">{authorInfo.title}</div>
                    <p className="author-bio">{authorInfo.bio}</p>
                  </div>
                </div>
              </div>

              {/* Sidebar - Stays on right on desktop, moves below on mobile */}
              <aside className="article-sidebar">
                {/* Related Articles */}
                {relatedBlogs.length > 0 && (
                  <div className="sidebar-card">
                    <div className="sidebar-card-header">
                      <CategoryIcon />
                      <h3>Related Articles</h3>
                    </div>
                    <div className="sidebar-card-content">
                      {relatedBlogs.map((relatedBlog) => (
                        <Link
                          key={relatedBlog._id}
                          to={`/blog/${relatedBlog.slug}`}
                          className="sidebar-article"
                        >
                          <div className="sidebar-article-image">
                            <img
                              src={
                                relatedBlog.featuredImage ||
                                getAvatarUrl(relatedBlog.title)
                              }
                              alt={relatedBlog.title}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = getAvatarUrl(relatedBlog.title);
                              }}
                            />
                          </div>
                          <div className="sidebar-article-content">
                            <h4>{truncateText(relatedBlog.title, 60)}</h4>
                            <div className="sidebar-article-meta">
                              <AccessTimeIcon />
                              <span>{relatedBlog.readTime}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Articles */}
                {popularBlogs.length > 0 && (
                  <div className="sidebar-card">
                    <div className="sidebar-card-header">
                      <TrendingUpIcon />
                      <h3>Popular Articles</h3>
                    </div>
                    <div className="sidebar-card-content">
                      {popularBlogs.map((popularBlog, index) => (
                        <Link
                          key={popularBlog._id}
                          to={`/blog/${popularBlog.slug}`}
                          className="sidebar-list-article"
                        >
                          <span className="article-number">{index + 1}</span>
                          <div className="article-info">
                            <h4>{truncateText(popularBlog.title, 50)}</h4>
                            <div className="article-stats-small">
                              <span>
                                <VisibilityIcon /> {popularBlog.views}
                              </span>
                              <span>
                                <FavoriteIcon /> {popularBlog.likes}
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Articles */}
                {recentBlogs.length > 0 && (
                  <div className="sidebar-card">
                    <div className="sidebar-card-header">
                      <ScheduleIcon />
                      <h3>Recent Articles</h3>
                    </div>
                    <div className="sidebar-card-content">
                      {recentBlogs.map((recentBlog) => (
                        <Link
                          key={recentBlog._id}
                          to={`/blog/${recentBlog.slug}`}
                          className="sidebar-list-article"
                        >
                          <div className="article-info">
                            <h4>{truncateText(recentBlog.title, 50)}</h4>
                            <div className="article-date-small">
                              {formatDate(recentBlog.publishedAt)}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </section>

        {/* Related Posts Section */}
        {relatedBlogs.length > 0 && (
          <section className="more-articles-section">
            <div className="container">
              <h2>Continue Reading</h2>
              <div className="more-articles-grid">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog._id}
                    to={`/blog/${relatedBlog.slug}`}
                    className="more-article-card"
                  >
                    <div className="more-card-image">
                      <img
                        src={
                          relatedBlog.featuredImage ||
                          getAvatarUrl(relatedBlog.title)
                        }
                        alt={relatedBlog.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = getAvatarUrl(relatedBlog.title);
                        }}
                      />
                      <div className="more-card-overlay">
                        <span className="more-category">
                          {relatedBlog.category}
                        </span>
                      </div>
                    </div>
                    <div className="more-card-content">
                      <h3>{relatedBlog.title}</h3>
                      <p>{relatedBlog.excerpt}</p>
                      <div className="more-card-meta">
                        <span>{getAuthorName(relatedBlog.author)}</span>
                        <span>·</span>
                        <span>{relatedBlog.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default SingleBlogPost;